const { vestResolver } = require('@hookform/resolvers/vest')
import { Button } from '@vtex/admin-ui'
import { FormState, useFormState, useWatch } from '@vtex/admin-ui-form'
import { useCallback, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  UseDrawerStateReturn,
} from 'ui'
import {
  BuyAndWinBenefitVariant,
  type BuyAndWinBenefit,
} from '../../../../../../typings'
import { BenefitSelector } from '../../../../../BenefitSelector'
import { ShippingVariantSelector } from '../../../../../ShippingVariantSelector'
import { BuyAndWinBenefitOptions, EMPTY_BENEFIT_BY_TYPE } from '../constants'
import messages from '../messages'
import { suite } from '../validation'
import { GiftSettings } from './Gift'
import { MaxPriceSettings } from './MaxPrice'
import styles from './styles'

interface Props {
  state: UseDrawerStateReturn
  config: BuyAndWinBenefit
  onSave: (config?: BuyAndWinBenefit) => void
}

export interface SettingsProps {
  state: FormState
}

const settingsComponentByType: Record<
  BuyAndWinBenefitVariant,
  (props: SettingsProps) => JSX.Element
> = {
  [BuyAndWinBenefitVariant.GIFT]: GiftSettings,
  [BuyAndWinBenefitVariant.MAXIMUM_UNIT_PRICE_DISCOUNT]: MaxPriceSettings,
  [BuyAndWinBenefitVariant.FREE_SHIPPING]: ShippingVariantSelector,
}

function SettingsComponent(props: SettingsProps) {
  const { state } = props

  const type: BuyAndWinBenefitVariant = useWatch({ form: state, name: 'type' })

  const Component = settingsComponentByType[type]

  if (!Component) return null

  return <Component {...props} />
}

export function BuyAndWinBenefitEditor(props: Props) {
  const { state, config, onSave } = props
  const { hide } = state
  const intl = useIntl()
  const { $t } = intl

  const settingsForm = useFormState<BuyAndWinBenefit>({
    defaultValues: { ...config },
    resolver: vestResolver(suite(intl)),
  })

  const currentType = useWatch({ form: settingsForm, name: 'type' })

  const setInitialConfig = useCallback(() => {
    const initialConfig = { ...config }
    settingsForm.reset(initialConfig)
  }, [config, settingsForm])

  useEffect(() => {
    state.open && setInitialConfig()
  }, [setInitialConfig, state.open])

  const handleSave = async () => {
    const isValid = await settingsForm.trigger()
    if (!isValid) return

    const values = settingsForm.getValues()

    switch (values.type) {
      case BuyAndWinBenefitVariant.GIFT:
        onSave(values.items.length ? values : undefined)
        break
      case BuyAndWinBenefitVariant.MAXIMUM_UNIT_PRICE_DISCOUNT:
        onSave(values?.price ? values : undefined)
      case BuyAndWinBenefitVariant.FREE_SHIPPING:
        onSave(values)
        break
    }
    hide()
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.title)}</DrawerTitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <div className={styles.content}>
          <BenefitSelector
            value={currentType}
            onChange={(type) => settingsForm.reset(EMPTY_BENEFIT_BY_TYPE[type])}
            options={BuyAndWinBenefitOptions}
          />
          <SettingsComponent state={settingsForm} />
        </div>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={state.hide} />
        <Button variant="primary" onClick={handleSave}>
          <FormattedMessage {...messages.drawer.apply} />
        </Button>
      </DrawerFooter>
    </Drawer>
  )
}
